import { createContext, useContext, useEffect, useRef, useState } from "react"
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  sendEmailVerification
} from "firebase/auth"
import { auth, db } from "../firebase/config"
import { createUserProfile, createVisit, getUserData } from "../firebase/users"
import { useAtom } from "jotai"
import { cartListAtom, userAtom } from "../store"
import { updateDoc, doc } from "firebase/firestore"
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { memberships } from "../utils/data";



export const authContext = createContext()

export const useAuth = () => {
  const context = useContext(authContext)
  if (!context) throw new Error("There is not auth provider")
  return context
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [userData, setUserData] = useAtom(userAtom)
  const [cartList, setCartList] = useAtom(cartListAtom)
  const membershipsForUser = memberships

  const getCartStorageKey = (uid) => uid ? `cartList:${uid}` : 'cartList:guest'

  const normalizeCartItems = (items = []) => {
    const cartMap = new Map()

    const normalizeKey = (item) => {
      if (!item) return 'unknown'
      if (item.id) return `id:${item.id}`
      if (item.shopify_variant_id || item.variant_id || item.variantId) return `variant:${item.shopify_variant_id || item.variant_id || item.variantId}`
      return `title:${item.title || 'untitled'}:${item.price || 0}`
    }

    const addItem = (item) => {
      if (!item) return

      const key = normalizeKey(item)
      const currentItem = cartMap.get(key)
      const nextCount = Number(item.count || 1)

      if (currentItem) {
        cartMap.set(key, {
          ...currentItem,
          ...item,
          count: Math.max(Number(currentItem.count || 1), nextCount)
        })
      } else {
        cartMap.set(key, { ...item, count: nextCount })
      }
    }

    ;(Array.isArray(items) ? items : []).forEach(addItem)

    return Array.from(cartMap.values())
  }

  const mergeCartLists = (savedCart = [], firestoreCart = []) => normalizeCartItems([
    ...savedCart,
    ...firestoreCart
  ])

  useEffect(() => {
    if (!user) {
      const guestCart = localStorage.getItem(getCartStorageKey())
      if (guestCart) {
        try {
          setCartList(normalizeCartItems(JSON.parse(guestCart)))
        } catch (error) {
          console.error('Failed to parse guest cart:', error)
        }
      }
      return
    }

    const cartKey = getCartStorageKey(user.uid)
    const firestoreCart = Array.isArray(userData?.cartList) ? userData.cartList : []
    const savedCart = localStorage.getItem(cartKey)

    try {
      const parsedSavedCart = savedCart ? JSON.parse(savedCart) : []
      const mergedCart = mergeCartLists(parsedSavedCart, firestoreCart)

      if (mergedCart.length > 0) {
        setCartList(mergedCart)
        localStorage.setItem(cartKey, JSON.stringify(mergedCart))
      } else if (parsedSavedCart.length > 0) {
        setCartList(normalizeCartItems(parsedSavedCart))
      } else if (firestoreCart.length > 0) {
        setCartList(normalizeCartItems(firestoreCart))
      }
    } catch (error) {
      console.error('Failed to parse saved user cart:', error)
      if (firestoreCart.length > 0) {
        setCartList(normalizeCartItems(firestoreCart))
      }
    }
  }, [user?.uid, userData?.cartList])

  useEffect(() => {
    const normalizedCart = normalizeCartItems(cartList)
    const cartKey = getCartStorageKey(user?.uid || null)
    localStorage.setItem(cartKey, JSON.stringify(normalizedCart))

    if (user?.uid && userData?.id === user.uid) {
      const userRef = doc(db, 'users', user.uid)
      updateDoc(userRef, { cartList: normalizedCart }).catch((error) => {
        console.error('Failed to sync cart to Firestore:', error)
      })
    }
  }, [cartList, user?.uid, userData?.id])
  
  useEffect(() => {
    const storedUserData = localStorage.getItem('userData')

    if (storedUserData) {
      setUserData(JSON.parse(storedUserData))
    }

    // if ((!storedUserData && !user) && !window.location.pathname.includes('/login') && !window.location.pathname.includes('/marketplace/categories')) {
    //   window.location.href = window.location.protocol + '//' + window.location.host + '/login'
    // }
  }, [window.location.href])

  // Read by the effect below's cleanup only -- kept out of that effect's
  // deps so a cart change doesn't tear down and recreate the auth
  // subscription (onAuthStateChanged fires immediately on (re)subscribe,
  // which schedules a fresh getUserData -> setUserData with a new cartList
  // array reference, which re-triggers the cart-merge effect's setCartList,
  // which changed `cartList` again -- an infinite ~1s loop of Firestore
  // reads/writes if `cartList` were a dependency here).
  const latestSessionRef = useRef({ user, cartList })
  useEffect(() => {
    latestSessionRef.current = { user, cartList }
  }, [user, cartList])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser)
      setLoading(false)

      if (currentUser) {
        setTimeout(() => {
          getUserData(currentUser.uid).then((data) => {
            createVisit({ user: data }).then(() => {

            }).catch((e) => {

            })

            setUserData({ ...data, id: currentUser.uid })
            localStorage.setItem('userData', JSON.stringify({ ...data, id: currentUser.uid }))
          })
        }, 1000)
      } else {
        // Clear user data from localStorage if no user is signed in
        localStorage.removeItem('userData')
      }
    })

    return () => {
      unsubscribe()
      const { user: latestUser, cartList: latestCartList } = latestSessionRef.current
      localStorage.setItem(getCartStorageKey(latestUser?.uid || null), JSON.stringify(latestCartList))
    }
  }, [])

  const isMembershipExpired = (renewDate) => {
    const date = new Date(renewDate.seconds * 1000);

    // Get today's date and add one month to it
    const oneMonthBeforeNow = new Date();
    oneMonthBeforeNow.setMonth(oneMonthBeforeNow.getMonth() - 1)

    return date < oneMonthBeforeNow
  }

  const signup = async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      const user = userCredential.user
      await createUserProfile(user.uid, user.email)
      toast.success("User signed up successfully")

      if (user) {
        sendEmailVerification(user)
        toast.info("Verification email sent. Please check your inbox.")
      } else {
        console.log("User creation failed. User is null.")
      }
    } catch (error) {
      if (error.code === "auth/email-already-in-use") {
        toast.error("This email address is already in use. Please use a different email or log in.")
      }
      if (error.code === "auth/invalid-email") {
        toast.error("The email address is not valid. Please enter a valid email.")
      }
      if (error.code === "auth/weak-password") {
        toast.error("The password is too weak. Please choose a stronger password.")
      }
      if (error.code === "auth/user-token-expired") {
        toast.error("Your session has expired. Please log in again.")
      }
    }
  }

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password)
  }

  const logout = () => {
    signOut(auth)
  }

  const loginWithGoogle = async () => {
    const googleProvider = new GoogleAuthProvider()

    try {
      const result = await signInWithPopup(auth, googleProvider)
      const user = result.user

      await createUserProfile(user.uid, user.email)
    } catch (error) {
      console.error("Error during sign-in:", error.code, error.message)
    }
  }

  const resetPassword = (email) => {
    sendPasswordResetEmail(auth, email)
  }

  const getUpdatedUserData = () => {
    if (user && user.uid) {
      getUserData(user.uid).then((data) => {
        setUserData({ ...data, id: user.uid })
        localStorage.setItem('userData', JSON.stringify({ ...data, id: user.uid }))
      })
    }
  }

  return (
    <authContext.Provider
      value={{
        signup,
        login,
        membershipsForUser,
        user,
        logout,
        loading,
        loginWithGoogle,
        resetPassword,
        getUpdatedUserData
      }}
    >
      {children}
    </authContext.Provider>
  )
}
